/**
 * CABEmptyState — Empty state displays for the CAB preview panel.
 */

import React from 'react';
import { Text, Icon } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';

interface CABEmptyStateProps {
  variant: 'no-files' | 'ready-to-generate' | 'processing';
  fileCount?: number;
}

export const CABEmptyState: React.FC<CABEmptyStateProps> = ({
  variant,
  fileCount = 0,
}) => {
  if (variant === 'processing') {
    return (
      <div className="flex flex-col items-center justify-center p-bg-v2-3xl text-center h-full">
        {/* Spinner */}
        <div className="w-12 h-12 border-3 border-border-secondary border-t-bg-brand-solid rounded-full animate-spin mb-bg-v2-xl" />

        <Text variant="text-md" weight="semibold" className="text-text-primary mb-bg-v2-sm text-center">
          Generating Blueprint
        </Text>
        <Text variant="text-sm" className="text-text-secondary max-w-xs leading-relaxed text-center">
          We&apos;re analyzing your uploaded documents and source code.
          The blueprint preview will appear here once generation is complete.
        </Text>
      </div>
    );
  }

  if (variant === 'ready-to-generate') {
    return (
      <div className="flex flex-col items-center justify-center p-bg-v2-3xl text-center h-full">
        <div className="w-18 h-18 rounded-full bg-bg-secondary flex items-center justify-center mb-bg-v2-xl">
          <Icon name={IconName.History} size="xl" className="!text-text-tertiary" inheritColor />
        </div>
        <Text variant="text-md" weight="semibold" className="text-text-primary mb-bg-v2-sm text-center">
          Ready to Generate
        </Text>
        <Text variant="text-sm" className="text-text-secondary max-w-xs leading-relaxed text-center">
          {fileCount} file{fileCount !== 1 ? 's' : ''} uploaded and ready.
          Click &quot;Generate Blueprint&quot; to start the analysis process.
        </Text>
      </div>
    );
  }

  // Default: no-files
  return (
    <div className="flex flex-col items-center justify-center p-bg-v2-3xl text-center h-full">
      <div className="w-18 h-18 rounded-full bg-bg-secondary flex items-center justify-center mb-bg-v2-xl">
        <Icon name={IconName.Document} size="xl" className="!text-text-tertiary" inheritColor />
      </div>
      <Text variant="text-md" weight="semibold" className="text-text-primary mb-bg-v2-sm text-center">
        No Blueprint Yet
      </Text>
      <Text variant="text-sm" className="text-text-secondary max-w-xs leading-relaxed text-center">
        Upload your documents and source code, then click &quot;Generate Blueprint&quot;
        to create your Common Application Blueprint.
      </Text>
    </div>
  );
};
